import NoTaskImage from "../../assets/png/NoTaskImage.png";
import NoSearchTask from "../../assets/png/NoSearchTask.png";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import Riskform from "./risk-form";
import useCreateDemoRiskQuary from "@/api/query/useCreateDemoRiskQuary";
import { useUser } from "@/hooks/useUser";
import Loader from "@/components/common/Loader";
import WbsImport from "./wbsImport";
import useProjectQuery from "@/api/query/useProjectQuery";
import { UserRoleEnumValue } from "@backend/src/schemas/enums";

type Props = {
  projectId: string;
  refetch: () => void;
  filtered: boolean;
  noShowImage: boolean;
};
function NoRisk(props: Props) {
  const [isOpenPopUp, setIsOpenPopUp] = useState(false);
  const { user } = useUser();
  const { t } = useTranslation();
  const useCreateDemoRisk = useCreateDemoRiskQuary(
    user?.userOrganisation[0].organisationId ?? "",
    props.projectId
  );
  const CreateDemoRisk = () => {
    useCreateDemoRisk.refetch().then(() => {
      setIsOpenPopUp(false);
      props.refetch();
    });
  };
  const projects = useProjectQuery();
  const isCollaborator = () => {
    const project = projects.data?.data.data.find(
      (p) => p.projectId == props.projectId
    );
    return project?.assignedUsers.some(
      (u) =>
        u.user.userId === user?.userId &&
        u.projectRole === UserRoleEnumValue.COLLABORATOR
    );
  };

  const isMember = () => {
    const project = projects.data?.data.data.find(
      (p) => p.projectId == props.projectId
    );
    return project?.assignedUsers.some(
      (u) =>
        u.user.userId === user?.userId &&
        u.projectRole === UserRoleEnumValue.TEAM_MEMBER
    );
  };

  const NoDataFoundIcon = () => (
    <svg
      width="500"
      height="250"
      viewBox="0 0 300 250"
      xmlns="http://www.w3.org/2000/svg"
      className="scale-75"
    >
      <defs>
        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feOffset result="offOut" in="SourceAlpha" dx="0" dy="4" />
          <feGaussianBlur result="blurOut" in="offOut" stdDeviation="4" />
          <feBlend in="SourceGraphic" in2="blurOut" mode="normal" />
        </filter>
      </defs>
      <style>
        {`
          .icon-base {
            stroke: #B0B0B0;
            stroke-width: 2.5;
            stroke-linecap: round;
            stroke-linejoin: round;
            fill: none;
          }
          .icon-fill {
            fill: #F5F5F5;
            filter: url(#shadow);
          }
          .void-circle {
            fill: #D0D0D0;
            stroke: none;
          }
          .void-line {
            stroke: #A0A0A0;
            stroke-width: 3;
            stroke-linecap: round;
          }
          .text-message {
            font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
            font-size: 24px;
            fill: #666;
            font-weight: 600;
            letter-spacing: 0.5px;
          }
        `}
      </style>

      {/* Group for the main icon elements */}
      <g transform="translate(0, -10)">
        {/* Document rectangle */}
        <rect
          x="70"
          y="70"
          width="120"
          height="110"
          rx="8"
          ry="8"
          className="icon-fill icon-base"
        />
        {/* Folded corner */}
        <path
          d="M 150 70 L 190 110 L 150 110 Z"
          className="icon-fill icon-base"
        />

        {/* Magnifying glass */}
        <circle cx="150" cy="125" r="30" className="icon-base" />
        <line x1="170" y1="145" x2="210" y2="185" className="icon-base" />
        <path d="M 190 170 C 195 180, 205 185, 210 185" className="icon-base" />

        {/* Void circle with dash */}
        <circle cx="130" cy="125" r="20" className="void-circle" />
        <line x1="115" y1="125" x2="145" y2="125" className="void-line" />
      </g>

      {/* Message */}
      <text
        x="150"
        y="225"
        textAnchor="middle"
        dominantBaseline="middle"
        className="text-message"
      >
        {t("No risks have been reported yet.")}
      </text>
    </svg>
  );
  return (
    <div className="w-full h-full p-8 ">
      {(useCreateDemoRisk.isFetching || useCreateDemoRisk.isRefetching) && (
        <Loader className="absolute top-0 left-0 h-full w-full z-10" />
      )}
      <div
        className={`w-full h-full flex overflow-auto justify-center rounded-lg`}
      >
        {isCollaborator() || isMember() ? (
          <div className="flex flex-col justify-center gap-10 items-center space-x-2 text-gray-600 shad">
            {/* Warning/Info SVG Icon */}
            <div>
              <NoDataFoundIcon />
            </div>
            {/* Localized Text */}
          </div>
        ) : (
          <div className="flex flex-col justify-center items-center ">
            <div className="text-center text-3xl text-gray-450/40 font-bold">
              {props.filtered ? "Oops!!! " : ""}
            </div>
            {props.filtered && (
              <div className="text-center text-2xl">
                {t("no_risk_with_filter")}
              </div>
            )}
            {Boolean(!props.noShowImage) && (
              <div className="p-6">
                <img
                  src={props.filtered ? NoSearchTask : NoTaskImage}
                  className="w-80"
                />
              </div>
            )}
            {
              <div className="lg:flex flex-col justify-between gap-x-5 w-full">
                {!props.filtered && (
                  <span className="text-neutral-600 text-3xl font-bold text-center">
                    {t("create_your_first_risk")}
                  </span>
                )}
                <div className="flex items-center ">
                  <Button
                    variant={"primary"}
                    onClick={() => setIsOpenPopUp(true)}
                    className="!w-full !mt-3 !tracking-wide !lg:mt-0 !lg:w-fit "
                  >
                    {t("create")}
                  </Button>
                </div>
              </div>
            }
            {!props.filtered && (
              <div
                className="text-center text-sm text-primary-500/80 underline underline-offset-2 cursor-pointer mt-5"
                onClick={CreateDemoRisk}
              >
                {t("Or load our sample data form")}
              </div>
            )}
            <div className="mt-2">
              <WbsImport projectId={props.projectId} refetch={props.refetch} />
            </div>
          </div>
        )}
      </div>
      {isOpenPopUp && (
        <Riskform
          projectId={props.projectId}
          setClose={(data, type) => {
            if (type === "update") {
              setIsOpenPopUp(data);
              props.refetch();
            } else {
              setIsOpenPopUp(data);
            }
          }}
          isOpen={isOpenPopUp}
          riskId={""}
        />
      )}
    </div>
  );
}

export default NoRisk;
